import { useState } from "react";
import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Sparkles } from "lucide-react";
import { Button } from "~/atoms/button";
import { Card } from "~/atoms/card";
import { Skeleton } from "~/atoms/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "~/atoms/tooltip";
import { ClampHtml } from "~/molecules/ClampHtml";
import { useDiffSummary } from "~/api/swr/useDiffSummary";
import { generateDiffSummary } from "~/api/v2/requests";
import type { DocumentDetailsVersion } from "~/api/types";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";
import { getPreviousVersion } from "~/helpers/document";

type PreviousVersionInfo = {
  version: DocumentDetailsVersion | undefined;
  versionNumber: number;
};

export function useDiffSummaryVisible(
  selectedVersion: DocumentDetailsVersion,
  documentVersions: DocumentDetailsVersion[],
):
  | { diffSummaryVisible: true; previousVersionInfo: PreviousVersionInfo }
  | { diffSummaryVisible: false } {
  const isFeatureEnabled = useFeatureFlagEnabled(featureFlags.diffSummary);
  const previousVersionInfo = getPreviousVersion(
    selectedVersion,
    documentVersions,
  );
  if (
    isFeatureEnabled &&
    previousVersionInfo &&
    selectedVersion.fileType === "docx"
  ) {
    return { diffSummaryVisible: true, previousVersionInfo };
  }
  return { diffSummaryVisible: false };
}

interface Props {
  documentId: number;
  versionId: number;
  fileUpdatedAt: string | null;
  previousVersionInfo: PreviousVersionInfo;
}

export const DiffSummary = ({
  documentId,
  versionId,
  fileUpdatedAt,
  previousVersionInfo,
}: Props) => {
  const { t } = useTranslation();
  const [isGenerating, setIsGenerating] = useState(false);
  const [justGenerated, setJustGenerated] = useState(false);

  const {
    data: diffSummary,
    isLoading: isDiffSummaryLoading,
    mutate: mutateDiffSummary,
  } = useDiffSummary(versionId);

  const canGenerateSummary =
    fileUpdatedAt &&
    (!diffSummary?.updatedAt ||
      new Date(diffSummary.updatedAt).getTime() <
        new Date(fileUpdatedAt).getTime());

  const handleGenerateSummary = async () => {
    setIsGenerating(true);
    try {
      await generateDiffSummary(versionId);
      const updated = await mutateDiffSummary();

      if (!updated) {
        toast.info(t(($) => $.toasts.info.noDiffSummaryAvailable));
        setJustGenerated(false);
        return;
      }
      setJustGenerated(true);
    } catch (error) {
      console.error("Failed to generate diff summary:", error);
      toast.error(t(($) => $.toasts.error.diffSummaryFailed));
    } finally {
      setIsGenerating(false);
    }
  };

  if (isDiffSummaryLoading) return <Skeleton className="h-32 rounded-xl" />;

  return (
    <Card className="bg-secondary/50 gap-2 p-4">
      {diffSummary ? (
        <>
          <div className="text-sm font-semibold">
            {t(($) => $.commons.workflow.diffSummary.changesComparedTo)}{" "}
            <Link
              to={`/documents/${documentId}?versionId=${previousVersionInfo.version?.id}`}
              className="text-primary hover:underline"
            >
              {t(($) => $.commons.workflow.versionOrder, {
                order: previousVersionInfo.versionNumber,
              })}
            </Link>
          </div>
          {diffSummary.summary === "" ? (
            <div className="text-muted-foreground text-sm font-light">
              {t(($) => $.commons.workflow.diffSummary.noChangesFound, {
                previousVersionNumber: previousVersionInfo.versionNumber,
                currentVersionNumber: previousVersionInfo.versionNumber + 1,
              })}
            </div>
          ) : (
            <ClampHtml html={diffSummary.summary} expand={justGenerated} />
          )}
        </>
      ) : (
        <div className="flex flex-col items-center justify-center gap-1 text-center">
          <div className="text-muted-foreground text-sm font-light">
            {t(($) => $.commons.workflow.diffSummary.emptyState.description, {
              previousVersionNumber: previousVersionInfo.versionNumber,
              currentVersionNumber: previousVersionInfo.versionNumber + 1,
            })}
          </div>
        </div>
      )}
      <div className="flex justify-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex w-full max-w-xs">
                <Button
                  onClick={handleGenerateSummary}
                  disabled={isGenerating || !canGenerateSummary}
                  className="ai-gradient-button text-primary-foreground relative w-full overflow-hidden font-semibold"
                >
                  <Sparkles className="size-5" />
                  {isGenerating
                    ? t(($) => $.commons.buttons.generatingDiffSummary)
                    : !canGenerateSummary
                      ? t(($) => $.commons.buttons.diffSummaryUpToDate)
                      : t(($) => $.commons.buttons.generateDiffSummary)}
                </Button>
              </span>
            </TooltipTrigger>
            {!canGenerateSummary && (
              <TooltipContent>
                <p>
                  {t(($) => $.commons.workflow.diffSummary.upToDateTooltip)}
                </p>
              </TooltipContent>
            )}
          </Tooltip>
        </TooltipProvider>
      </div>
    </Card>
  );
};
